<script>
import ItacLogo from './assets/logos/ItacLogo.vue';
import MenuMobileItem from './components/general/MenuMobileItem.vue';
import { useUserStore } from './store/userStore';
import { usePeopleStore } from './store/peopleStore';
import { useRecoStore } from './store/recoStore';
import { useSocketStore } from './store/socketStore';
import ProfileInitials from './components/general/Profile/ProfileInitials.vue';
import ProfileModal from './components/general/Profile/ProfileModal.vue';
import ChangelogModal from './components/general/ChangelogModal.vue';
import { Toaster } from 'vue-sonner';
import 'vue-sonner/style.css';
import { useRouter } from 'vue-router';
import { useVideoStore } from './store/videoStore';
import { useFeatureFlag } from './composables/useFeatureFlag';

export default {
  components: {
    MenuMobileItem,
    ItacLogo,
    ProfileInitials,
    ProfileModal,
    ChangelogModal,
    Toaster,
  },
  setup() {
    const userStore = useUserStore();
    const peopleStore = usePeopleStore();
    const recoStore = useRecoStore();
    const socketStore = useSocketStore();
    const videoStore = useVideoStore();
    const router = useRouter();
    const isAlcinosEnabled = useFeatureFlag('alcinos');
    const isRmsEnabled = useFeatureFlag('rms');

    return { userStore, peopleStore, recoStore, socketStore, videoStore, router, isAlcinosEnabled, isRmsEnabled };
  },
  data() {
    return {
      profileModalVisible: false,
      changelogModalVisible: false,
      changelogUnread: false,
    };
  },
  watch: {
    $route(to) {
      document.title = to.meta.title || 'Itac';
    },
    'userStore.maintenanceMode'(enabled) {
      // Le flag Unleash est pollé toutes les 60s : on (re)dirige selon son état
      this.applyMaintenanceState(enabled);
    },
  },
  async mounted() {
    document.title = this.$route.meta.title || 'Itac';
    // Le flag peut déjà être à true au montage (réponse Unleash plus rapide que le mount) :
    // dans ce cas le watcher ne détecte aucun changement, d'où ce contrôle explicite.
    this.applyMaintenanceState(this.userStore.maintenanceMode);
    this.checkChangelogVersion();
  },
  methods: {
    applyMaintenanceState(enabled) {
      if (enabled) {
        if (!this.userStore.isAdmin) {
          this.router.push({ name: 'maintenance' });
        }
      } else if (this.$route.name === 'maintenance') {
        this.router.push({ name: 'home' });
      }
    },
    openProfileModal() {
      this.profileModalVisible = true;
    },
    checkChangelogVersion() {
      const seenVersion = localStorage.getItem('itac_changelog_seen_version');
      const currentVersion = process.env.VUE_APP_VERSION;
      if (seenVersion !== currentVersion) {
        this.changelogUnread = true;
        this.changelogModalVisible = true;
      }
    },
    openChangelog() {
      this.changelogModalVisible = true;
    },
    closeChangelog() {
      this.changelogModalVisible = false;
      this.changelogUnread = false;
      localStorage.setItem('itac_changelog_seen_version', process.env.VUE_APP_VERSION);
    },
  },
};
</script>

<template>
  <nav v-if="!$route.meta.hideMenu" class="app-nav-bar">
    <router-link id="home-link" to="/">
      <ItacLogo id="home-link-logo" />
    </router-link>
    <transition name="fade-right" mode="out-in">
      <MenuMobileItem class="menu-mobile" />
    </transition>
    <div class="routes">
      <router-link
        v-if="!$route.meta.hideNav"
        class="nav-link"
        to="/argos"
        :class="[{ 'router-link-active': $route.name === 'argos' }]">
        {{ $t('main-menu.reco') }}
      </router-link>
      <router-link
        v-if="!$route.meta.hideNav"
        class="nav-link"
        to="/ulis"
        :class="[{ 'router-link-active': $route.name === 'ulis' }]">
        {{ $t('main-menu.recup') }}
      </router-link>
      <router-link
        v-if="!$route.meta.hideNav && isAlcinosEnabled"
        class="nav-link"
        to="/alcinos"
        :class="[{ 'router-link-active': $route.name === 'alcinos' }]">
        {{ $t('main-menu.event') }}
      </router-link>
      <router-link
        v-if="!$route.meta.hideNav && isRmsEnabled"
        class="nav-link"
        to="/rms"
        :class="[{ 'router-link-active': $route.name === 'rms' }]">
        {{ $t('main-menu.sub-video') }}
      </router-link>
      <router-link
        v-if="!$route.meta.hideNav && userStore.isAdmin"
        class="nav-link"
        to="/admin"
        :class="[{ 'router-link-active': $route.name === 'admin' }]">
        {{ $t('main-menu.admin') }}
      </router-link>
      <ProfileInitials :size="36" @click="openProfileModal" />
    </div>
  </nav>
  <Toaster
    position="top-right"
    :close-button="true"
    close-button-position="top-right"
    :expand="false"
    rich-colors
    :visible-toasts="3" />

  <ProfileModal
    v-if="profileModalVisible"
    :profile-modal-visible="profileModalVisible"
    :changelog-unread="changelogUnread"
    @close="profileModalVisible = false"
    @open-changelog="profileModalVisible = false; openChangelog()" />
  <ChangelogModal v-if="changelogModalVisible" @close="closeChangelog" />
  <router-view />
</template>

<style scoped>
#home-link {
  display: flex;
  align-items: center;
  justify-content: left;
  text-decoration: none;
  transition: scale 0.2s ease;
  margin: 10px;
}

#home-link-logo {
  height: 45px;
  transition: scale 0.2s ease;
}

@media (hover: hover) {
  #home-link:hover {
    scale: 1.1;
  }

  #home-link:hover #home-link-logo {
    scale: 1.1;
  }
}

.routes {
  display: flex;
  align-items: center;
}

.menu-mobile {
  display: none;
}

.app-nav-bar {
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-link {
  padding: 0 20px;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 1rem;
  color: var(--text-tertiary);
  transition: color 0.2s ease;
}

.nav-link.router-link-active {
  color: var(--selected-text);
}

.fade-right-enter-active,
.fade-right-leave-active {
  transition: all 0.2s ease-out;
}

.fade-right-enter-from,
.fade-right-leave-to {
  transform: scale(90%);
  opacity: 0;
}

.project-logo-text {
  font-size: 3rem;
  font-weight: bold;
  color: var(--text);
}

.logout-icon {
  color: var(--text-tertiary);
}

@media screen and (max-width: 769px) {
  .app-nav-bar {
    height: 60px;
  }

  #home-link-logo {
    height: 45px;
    padding: 10px;
    margin-top: 10px;
  }

  .routes {
    display: none;
  }

  .menu-mobile {
    display: block;
  }
}
</style>
